import React, { useEffect, useRef, useState } from "react";
import { Lock, RotateCcw, ShieldCheck } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  hasAdminPin, setAdminPin, verifyAdminPin, clearAdminPin,
  markAdminUnlocked,
} from "../lib/kioskConfig";

/**
 * Reusable PIN gate. Renders a dialog when `open` is true and only fires
 * `onUnlocked()` once the correct PIN is entered (or a new one is created).
 */
export default function AdminPinGate({ open, onOpenChange, onUnlocked }) {
  const { t } = useLang();
  const create = !hasAdminPin();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPin(""); setConfirm(""); setError("");
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  const submit = () => {
    setError("");
    if (create) {
      if (pin.length !== 4) { setError(t("pin_wrong")); return; }
      if (pin !== confirm) { setError(t("pin_mismatch")); return; }
      setAdminPin(pin);
      markAdminUnlocked();
      toast.success(t("saved"));
      onOpenChange(false);
      onUnlocked?.();
      return;
    }
    if (!verifyAdminPin(pin)) { setError(t("pin_wrong")); return; }
    markAdminUnlocked();
    onOpenChange(false);
    onUnlocked?.();
  };

  const reset = () => {
    if (!window.confirm(t("pin_reset_confirm"))) return;
    clearAdminPin();
    localStorage.removeItem("sakhi_trusted_adult");
    localStorage.removeItem("sakhi_photo");
    toast.success(t("saved"));
    setPin(""); setConfirm(""); setError("");
    // Now we're back in "create" mode
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-white">
        <DialogHeader>
          <div className="w-14 h-14 mx-auto rounded-2xl bg-primary text-white flex items-center justify-center mb-3">
            {create ? <ShieldCheck className="w-7 h-7" strokeWidth={2.5} /> : <Lock className="w-7 h-7" strokeWidth={2.5} />}
          </div>
          <DialogTitle className="text-xl font-bold text-center">
            {create ? t("pin_create_title") : t("pin_enter_title")}
          </DialogTitle>
          <DialogDescription className="text-center">
            {create ? t("pin_create_sub") : t("pin_enter_sub")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <Input
            ref={inputRef}
            data-testid="pin-input"
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            onKeyDown={(e) => { if (e.key === "Enter" && !create) submit(); }}
            placeholder="••••"
            className="h-16 text-center text-4xl font-bold tracking-[0.6em] rounded-2xl border-2 bg-white"
          />
          {create && (
            <Input
              data-testid="pin-confirm-input"
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder={t("pin_confirm")}
              className="h-16 text-center text-4xl font-bold tracking-[0.6em] rounded-2xl border-2 bg-white"
            />
          )}
          {error && (
            <div data-testid="pin-error" className="text-sm text-destructive text-center font-semibold">
              {error}
            </div>
          )}
          <Button
            data-testid="pin-submit"
            onClick={submit}
            className="w-full pill-btn pill-btn-primary h-auto"
          >
            {create ? t("save") : t("unlock")}
          </Button>
          {!create && (
            <button
              data-testid="pin-reset"
              onClick={reset}
              className="w-full text-xs text-muted-foreground hover:text-destructive flex items-center justify-center gap-1 pt-2"
            >
              <RotateCcw className="w-3 h-3" /> {t("pin_reset_hint")} {t("pin_reset_btn")}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
